import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  User,
  Plus,
  Edit,
  Trash2,
  Filter,
  ChevronDown,
} from "lucide-react";
import { format } from "date-fns";
import medicalDB from "../services/database";

const AppointmentList = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    date: searchParams.get("date") || "",
    status: searchParams.get("status") || "",
    type: searchParams.get("type") || "",
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, [filters]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await medicalDB.getAllAppointments(filters);
      setAppointments(data);
    } catch (error) {
      console.error("Error loading appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update URL search params
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newSearchParams.set(k, v);
    });
    setSearchParams(newSearchParams);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "ROUTINE":
        return "bg-medical-lightblue text-medical-blue dark:bg-medical-dark-lightblue dark:text-medical-dark-blue";
      case "FOLLOW_UP":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "NEW_PATIENT":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "CONSULTATION":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200";
      case "EMERGENCY":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const formatAppointmentTime = (date, startTime, endTime) => {
    const appointmentDate = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dateStr;
    if (appointmentDate.toDateString() === today.toDateString()) {
      dateStr = "Today";
    } else if (appointmentDate.toDateString() === tomorrow.toDateString()) {
      dateStr = "Tomorrow";
    } else {
      dateStr = format(appointmentDate, "MMM dd, yyyy");
    }

    return `${dateStr} • ${startTime} - ${endTime}`;
  };

  const getAppointmentTypeName = (type) => {
    const appointmentTypes = {
      ROUTINE: "Routine Checkup",
      FOLLOW_UP: "Follow-up",
      NEW_PATIENT: "New Patient",
      CONSULTATION: "Consultation",
      EMERGENCY: "Emergency",
    };
    return appointmentTypes[type] || type;
  };

  const getAppointmentDuration = (type) => {
    const appointmentTypes = {
      ROUTINE: 30,
      FOLLOW_UP: 20,
      NEW_PATIENT: 45,
      CONSULTATION: 30,
      EMERGENCY: 15,
    };
    return appointmentTypes[type] || 30;
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm("Are you sure you want to cancel this appointment?")) {
      try {
        await medicalDB.deleteAppointment(appointmentId);
        loadAppointments(); // Reload the list

        // Dispatch event to refresh sidebar schedule
        window.dispatchEvent(new CustomEvent("appointmentChanged"));
      } catch (error) {
        console.error("Error cancelling appointment:", error);
        alert("Failed to cancel appointment. Please try again.");
      }
    }
  };

  const handleCheckInAppointment = async (appointment) => {
    if (
      window.confirm(
        `Check in ${appointment.patient?.name} for their ${appointment.startTime} appointment?`
      )
    ) {
      try {
        // Mark appointment as completed
        await medicalDB.updateAppointment(appointment.id, {
          status: "completed",
        });

        // Reload appointments
        loadAppointments();

        // Dispatch event to refresh sidebar schedule
        window.dispatchEvent(new CustomEvent("appointmentChanged"));

        // Navigate to visit form with pre-filled data
        window.location.href = `/patients/${appointment.patientId}?newVisit=true&appointmentId=${appointment.id}`;
      } catch (error) {
        console.error("Error checking in appointment:", error);
        alert("Failed to check in appointment. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading appointments...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="h-8 w-8 text-medical-blue" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Appointments
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage patient appointments and schedule
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {appointments.length} appointments
          </span>

          <Link
            to="/appointments/calendar"
            className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Calendar size={20} />
            <span>Calendar</span>
          </Link>

          <Link
            to="/appointments/new"
            className="btn-primary flex items-center space-x-2"
            data-agent-action="add-appointment"
          >
            <Plus size={20} />
            <span>New Appointment</span>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Filters
          </h3>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Filter size={20} />
            <span>Filter</span>
            <ChevronDown
              size={16}
              className={`transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Date</label>
                <input
                  type="date"
                  value={filters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="form-input"
                >
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="form-label">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
                  className="form-input"
                >
                  <option value="">All Types</option>
                  <option value="ROUTINE">Routine Checkup</option>
                  <option value="FOLLOW_UP">Follow-up</option>
                  <option value="NEW_PATIENT">New Patient</option>
                  <option value="CONSULTATION">Consultation</option>
                  <option value="EMERGENCY">Emergency</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Appointments List */}
      {appointments.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No appointments found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {Object.values(filters).some((f) => f)
              ? "Try adjusting your filters"
              : "Get started by scheduling your first appointment"}
          </p>
          <Link
            to="/appointments/new"
            className="btn-primary"
            data-agent-action="add-appointment"
          >
            Schedule First Appointment
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {appointments.map((appointment) => (
            <div
              key={appointment.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-medical-lightblue rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-medical-blue" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate">
                        {appointment.patient?.name || "Unknown Patient"}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          appointment.status
                        )}`}
                      >
                        {appointment.status}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                          appointment.type
                        )}`}
                      >
                        {getAppointmentTypeName(appointment.type)}
                      </span>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Clock size={16} />
                        <span>
                          {formatAppointmentTime(
                            appointment.date,
                            appointment.startTime,
                            appointment.endTime
                          )}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar size={16} />
                        <span>
                          {getAppointmentDuration(appointment.type)} min
                        </span>
                      </div>
                    </div>

                    {appointment.notes && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 truncate">
                        {appointment.notes}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {appointment.status === "scheduled" && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleCheckInAppointment(appointment);
                      }}
                      className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                      title="Check in patient"
                    >
                      <User size={18} />
                    </button>
                  )}
                  <Link
                    to={`/appointments/${appointment.id}/edit`}
                    className="p-2 text-gray-400 hover:text-medical-blue transition-colors"
                    title="Edit appointment"
                  >
                    <Edit size={18} />
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteAppointment(appointment.id);
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Cancel appointment"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
